import { TrashIcon } from '@heroicons/react/outline';
import { Button, Card, Link, Title } from '@yoast/ui-library';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { useGetBrands, useGetCurrentUser } from '../../api/generated/insights';
import type { BrandsBrandsItem } from '../../api/generated/insights.schemas';
import CardHeader from '../../components/CardHeader';
import createComponent from '../../components/CreateComponent';
import DeleteAllDataModal from '../../components/DeleteAllDataModal';
import DeleteBrandModal from '../../components/DeleteBrandModal';
import Divider from '../../components/Divider';
import SettingsBrandRow from './SettingsBrandRow';

const SettingsRowHeader = createComponent`xl:yst-w-1/3`;
const SettingsRowContent = createComponent`xl:yst-w-2/3`;
const SettingsRow = createComponent`yst-flex yst-flex-col yst-gap-8 xl:yst-flex-row`;

const Settings = () => {
  // const [analysisNotification, setAnalysisNotification] = useState(true);
  // const [analysisFrequency, setAnalysisFrequency] = useState('weekly');

  // Modal state management
  const [showDeleteBrandModal, setShowDeleteBrandModal] = useState(false);
  const [showDeleteAllDataModal, setShowDeleteAllDataModal] = useState(false);
  const [selectedBrandId, setSelectedBrandId] = useState<string | null>(null);

  const { data: user } = useGetCurrentUser({
    query: {
      select: ({ data }) => data,
    },
  });

  const { data: brands } = useGetBrands({
    query: {
      select: ({ data }) => data?.brands,

      // force fresh data on mount/window focus/reconnect
      staleTime: 5 * 1000, // 5 seconds

      // Refetch when the window regains focus or the client reconnects.
      refetchOnWindowFocus: true,

      // refetch interval
      refetchInterval: 30 * 1000, // 30 seconds
    },
  });

  // Get the selected brand name for the modal
  const selectedBrand = brands?.find((brand) => brand.id === selectedBrandId);

  return (
    <form>
      <Card>
        <CardHeader
          title="Settings"
          description="Manage your brand settings and data. You can update analysis preferences or delete brands from your account."
        />
        <Card.Content>
          {/* 1 */}
          <SettingsRow>
            <SettingsRowHeader className="yst-space-y-2">
              <Title size="4">Brands</Title>
              <p>
                You have{' '}
                {/* TODO: should b/strong get this styling globally? If so, add to index.css */}
                <b className="yst-font-semibold yst-text-slate-800">
                  {user?.activeSubscriptions ?? '—'} subscription(s)
                </b>
                . This gives you up to{' '}
                <b className="yst-font-semibold yst-text-slate-800">
                  {user?.maxNumberOfActiveBrands ?? '—'} active brand(s)
                </b>
                , for which we&rsquo;ll automatically run a weekly analysis.
              </p>
              <p>
                You can activate or deactivate brands anytime, or permanently
                delete a brand and all its data.
              </p>
            </SettingsRowHeader>
            <SettingsRowContent>
              {brands && (
                <p className="yst-flex yst-w-full yst-max-w-2xl yst-flex-1 yst-flex-row yst-items-center yst-justify-start yst-gap-2 yst-border-b-[1px] yst-border-b-slate-200 yst-py-4 yst-font-medium yst-text-slate-800">
                  Active brands: {user?.numberOfActiveBrands ?? '—'} of{' '}
                  {user?.maxNumberOfActiveBrands ?? '—'}
                </p>
              )}

              {/* TODO: empty state */}
              {brands && brands.length === 0 && (
                <p className="yst-mt-4 yst-text-slate-500">
                  No brands found. Please{' '}
                  <Link as={RouterLink} to="/setup">
                    add a brand
                  </Link>{' '}
                  to start analyzing.
                </p>
              )}

              {/* ROW */}
              {(
                brands ??
                ([{ id: 'loading', name: 'Loading...' }] as BrandsBrandsItem[])
              ).map((brand, idx, arr) => (
                <SettingsBrandRow
                  key={brand.id}
                  brand={brand}
                  isLast={idx === arr.length - 1}
                  onDelete={(id) => {
                    setSelectedBrandId(id);
                    setShowDeleteBrandModal(true);
                  }}
                />
              ))}
            </SettingsRowContent>
          </SettingsRow>

          <Divider />

          {/* 2 */}
          {/*
          <div><SettingsRow>
              <SettingsRowHeader className="yst-space-y-2">
                <Title size="4">Analysis preferences</Title>
                <p>Decide your preferences for your brand analysis.</p>
              </SettingsRowHeader>

              <div className="yst-flex yst-flex-col yst-gap-8 md:yst-w-2/3">
                <div>
                  <Label htmlFor="analysis-frequency">Analysis frequency</Label>
                  <Select
                    id="analysis-frequency"
                    value={analysisFrequency}
                    // label="Analysis frequency"
                    options={[
                      { value: 'daily', label: 'Daily' },
                      {
                        value: 'weekly',
                        label: 'Weekly',
                      },
                      {
                        value: 'monthly',
                        label: 'Monthly',
                      },
                    ]}
                    onChange={(value: string) => {
                      setAnalysisFrequency(value);
                      console.log('Annual frequency changed to', value);
                    }}
                  />
                  <p>
                    The frequency of automatic analyses being performed based on
                    your latest analysis.
                  </p>
                </div>

                <div>
                  <div className="yst-flex yst-max-w-[384px] yst-justify-between yst-gap-4">
                    <Label htmlFor="analysis-notification">
                      Analysis notification
                    </Label>
                    <Toggle
                      id="analysis-notification"
                      label="Analysis notification"
                      checked={analysisNotification}
                      screenReaderLabel="Analysis notification"
                      onChange={(value: boolean) => {
                        setAnalysisNotification(value);
                        console.log('Analysis notification toggled', value);
                      }}
                    />
                  </div>
                  <p>
                    Send me an email when there is a new analysis available.
                  </p>
                </div>

                <div>
                  <Label htmlFor="email-address">Email address</Label>
                  <TextInput
                    id="email-address"
                    type="email"
                    placeholder="Defaultloginemail@example.com"
                  />
                  <p>
                    We send an email to the above email address in case an
                    analysis has occurred.
                  </p>
                </div>
              </div>
            </SettingsRow>
          </div>

          <Divider /> */}

          {/* 3 */}
          <SettingsRow>
            <SettingsRowHeader className="yst-space-y-2">
              <Title size="4">Delete all data</Title>
              <p>
                Permanently delete all brands, analysis results, and query
                history from your account.
              </p>
            </SettingsRowHeader>
            <SettingsRowContent className="yst-space-y-4">
              <Button
                variant="error"
                onClick={() => {
                  setShowDeleteAllDataModal(true);
                }}
                disabled={brands?.length === 0}
              >
                <TrashIcon /> Delete all data
              </Button>
              <p>
                <i>
                  <b>Note:</b> This will erase everything and cannot be
                  reversed.
                </i>
              </p>
            </SettingsRowContent>
          </SettingsRow>
        </Card.Content>
      </Card>

      {/* MODAL: DELETE BRAND */}
      <DeleteBrandModal
        isOpen={showDeleteBrandModal}
        onClose={() => {
          setShowDeleteBrandModal(false);
          setSelectedBrandId(null);
        }}
        brandId={selectedBrandId || ''}
        brandName={selectedBrand?.name}
      />

      {/* MODAL: DELETE ALL DATA */}
      <DeleteAllDataModal
        isOpen={showDeleteAllDataModal}
        onClose={() => {
          setShowDeleteAllDataModal(false);
        }}
      />
    </form>
  );
};

export default Settings;
